import { useMonitor } from "@suse-o11y/signal";
import React from "react";

import { MultiSelect } from "~/components/multi-select";
import { MultiSelectItem } from "~/components/multi-select/item";
import { RadioButtonGroup } from "~/components/radio-button-group";
import { RadioButtonGroupItem } from "~/components/radio-button-group/item";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { ALL_CALCULATIONS, type Calculation } from "~/tools/calculate-value-from-points";
import type { Record } from "~/tools/record";

import type { ModeFormState, PositionFormState, TableFormState } from "./form-state";
import { ALL_MODES, ALL_POSITIONS, type Mode, type Position } from "./settings";

// POSITION

const POSITION_TO_LABEL = {
	bottom: "Bottom",
	right: "Right",
} satisfies Record<Position>;

export interface WidgetLegendPositionProps {
	className?: string;
	disabled?: boolean;
	name: string;
	position: PositionFormState;
}

export const WidgetLegendPosition: React.FC<WidgetLegendPositionProps> = ({ className, disabled, name, position }) => {
	const monitor = useMonitor();

	return (
		<RadioButtonGroup
			className={className}
			disabled={disabled}
			onValueChange={(value) => position.setInput(value)}
			value={position.getInput(monitor)}
		>
			{ALL_POSITIONS.map((variant) => (
				<RadioButtonGroupItem key={variant} name={name} value={variant}>
					{POSITION_TO_LABEL[variant]}
				</RadioButtonGroupItem>
			))}
		</RadioButtonGroup>
	);
};

// MODE

const MODE_TO_LABEL = {
	list: "List",
	table: "Table",
} satisfies Record<Mode>;

export interface WidgetLegendModeProps {
	className?: string;
	disabled?: boolean;
	name: string;
	mode: ModeFormState;
}

export const WidgetLegendMode: React.FC<WidgetLegendModeProps> = ({ className, disabled, name, mode }) => {
	const monitor = useMonitor();

	return (
		<RadioButtonGroup
			className={className}
			disabled={disabled}
			onValueChange={(value) => mode.setInput(value)}
			value={mode.getInput(monitor)}
		>
			{ALL_MODES.map((variant) => (
				<RadioButtonGroupItem key={variant} name={name} value={variant}>
					{MODE_TO_LABEL[variant]}
				</RadioButtonGroupItem>
			))}
		</RadioButtonGroup>
	);
};

// TABLE

const CALCULATION_TO_LABELS = {
	mean: {
		full: "Average value",
		short: "Avg",
	},
	first: {
		full: "First value",
		short: "First",
	},
	"first-number": {
		full: "First numeric value",
		short: "First*",
	},
	last: {
		full: "Last value",
		short: "Last",
	},
	"last-number": {
		full: "Last numeric value",
		short: "Last*",
	},
	sum: {
		full: "Sum of the values",
		short: "Sum",
	},
	min: {
		full: "Minimum value",
		short: "Min",
	},
	max: {
		full: "Maximum value",
		short: "Max",
	},
} satisfies Record<Calculation>;

export interface WidgetLegendTableProps {
	className?: string;
	disabled?: boolean;
	table: TableFormState;
}

export const WidgetLegendTable: React.FC<WidgetLegendTableProps> = ({ className, disabled, table }) => {
	const monitor = useMonitor();

	return (
		<MultiSelect
			className={className}
			disabled={disabled}
			onValuesChange={(values) => table.setInput(values)}
			placeholder="Select additional table columns"
			values={table.getInput(monitor)}
		>
			{ALL_CALCULATIONS.map((variant) => {
				const { short, full } = CALCULATION_TO_LABELS[variant];

				return (
					<MultiSelectItem key={variant} value={variant}>
						<Tooltip>
							<TooltipTrigger>
								<span>{full}</span>
							</TooltipTrigger>
							<TooltipContent>{short}</TooltipContent>
						</Tooltip>
					</MultiSelectItem>
				);
			})}
		</MultiSelect>
	);
};
